import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Spinner } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Save, Lock, Landmark } from "lucide-react";

export default function CompanySettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setForm(r.data)).catch((e) => setErr(formatErr(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setSaved(false);
    try {
      const { data } = await api.put("/company", form);
      setForm(data); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!form) return <Layout title="Empresa"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Layout title="Empresa" subtitle="Identidade e dados fiscais usados no romaneio e NF-e">
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Razão social</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Razão Social" required value={form.razao_social} onChange={set("razao_social")} data-testid="company-razao" />
              <Input label="Nome Fantasia" value={form.nome_fantasia} onChange={set("nome_fantasia")} />
              <Input label="CNPJ" value={form.cnpj} onChange={set("cnpj")} data-testid="company-cnpj" />
              <Input label="Inscrição Estadual" value={form.ie} onChange={set("ie")} />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-1">Endereço</h3>
            <p className="text-xs text-ink-500 mb-4">Estes campos vão para a NF-e — preencha completo.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Logradouro" value={form.endereco_logradouro} onChange={set("endereco_logradouro")} />
              <Input label="Número" value={form.endereco_numero} onChange={set("endereco_numero")} />
              <Input label="Bairro" value={form.endereco_bairro} onChange={set("endereco_bairro")} />
              <Input label="Cidade" value={form.endereco_cidade} onChange={set("endereco_cidade")} />
              <Input label="UF" maxLength={2} value={form.endereco_uf} onChange={set("endereco_uf")} />
              <Input label="CEP" value={form.endereco_cep} onChange={set("endereco_cep")} />
              <Input label="Código IBGE do município" placeholder="7 dígitos" value={form.codigo_municipio_ibge} onChange={set("codigo_municipio_ibge")} data-testid="company-ibge" />
              <Input label="Inscrição Municipal (opcional)" value={form.im} onChange={set("im")} />
              <Input label="Telefone" value={form.telefone} onChange={set("telefone")} />
              <Input label="Email" type="email" value={form.email} onChange={set("email")} />
              <Input label="Logo URL (PNG/SVG)" value={form.logo_url} onChange={set("logo_url")} />
            </div>
          </Card>

          {/* Saldos de Caixa (ADM apenas) */}
          <Card className="p-6" data-testid="saldos-caixa-card">
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2">
                <Landmark size={18} className="text-brand-600" />
                <h3 className="font-heading text-lg font-bold text-ink-900">Saldo Inicial das Contas</h3>
              </div>
              {!isAdmin && (
                <div className="flex items-center gap-1.5 text-xs bg-stone-100 text-ink-600 px-2.5 py-1 rounded-md font-semibold">
                  <Lock size={12} /> Apenas administrador
                </div>
              )}
            </div>
            <p className="text-xs text-ink-500 mb-4">
              Valores de referência ("saldo inicial") das contas bancárias e caixa físico. O saldo real ao vivo é calculado no Financeiro somando recebimentos e subtraindo pagamentos. {isAdmin ? "Somente o ADM pode editar." : "Somente o administrador pode editar estes campos."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SaldoInput label="Sicredi" value={form.saldo_sicredi} onChange={set("saldo_sicredi")} disabled={!isAdmin} testid="saldo-sicredi" />
              <SaldoInput label="Banco do Brasil" value={form.saldo_banco_brasil} onChange={set("saldo_banco_brasil")} disabled={!isAdmin} testid="saldo-bb" />
              <SaldoInput label="Safra" value={form.saldo_safra} onChange={set("saldo_safra")} disabled={!isAdmin} testid="saldo-safra" />
              <SaldoInput label="Valor em Cheques" value={form.saldo_cheques} onChange={set("saldo_cheques")} disabled={!isAdmin} testid="saldo-cheques" />
              <SaldoInput label="Valor em Dinheiro" value={form.saldo_dinheiro} onChange={set("saldo_dinheiro")} disabled={!isAdmin} testid="saldo-dinheiro" />
              <div className="rounded-md bg-brand-50 border border-brand-200 p-3 flex flex-col justify-center">
                <div className="text-[11px] font-bold uppercase tracking-wider text-brand-700">Saldo Inicial Total</div>
                <div className="font-mono text-2xl font-bold text-brand-900 mt-0.5">
                  {(Number(form.saldo_sicredi || 0) + Number(form.saldo_banco_brasil || 0) + Number(form.saldo_safra || 0) + Number(form.saldo_cheques || 0) + Number(form.saldo_dinheiro || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </div>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-1">Fiscal / NF-e</h3>
            <p className="text-xs text-ink-500 mb-4">Parâmetros fiscais aplicados a todas as saídas ao emitir NF-e.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Regime tributário</label>
                <select value={form.regime_tributario || "lucro_real"} onChange={set("regime_tributario")} className="w-full rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900" data-testid="company-regime">
                  <option value="simples_nacional">Simples Nacional</option>
                  <option value="lucro_presumido">Lucro Presumido</option>
                  <option value="lucro_real">Lucro Real</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Ambiente Focus NF-e</label>
                <select value={form.focus_ambiente || "homologacao"} onChange={set("focus_ambiente")} className="w-full rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900" data-testid="company-focus-ambiente">
                  <option value="homologacao">Homologação (teste)</option>
                  <option value="producao">Produção (emite de verdade)</option>
                </select>
                <p className="text-[11px] text-ink-500 mt-1">Homologação não gera efeito fiscal — use para testes.</p>
              </div>
              <Input label="NCM padrão" placeholder="07031019" value={form.ncm_padrao} onChange={set("ncm_padrao")} data-testid="company-ncm" />
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Origem da mercadoria</label>
                <select value={form.origem_mercadoria || "0"} onChange={set("origem_mercadoria")} className="w-full rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900" data-testid="company-origem">
                  <option value="0">0 — Nacional</option>
                  <option value="1">1 — Estrangeira (Importação direta)</option>
                  <option value="2">2 — Estrangeira (adquirida no mercado interno)</option>
                  <option value="3">3 — Nacional com {'>'} 40% conteúdo importação</option>
                  <option value="4">4 — Nacional com processos produtivos</option>
                  <option value="5">5 — Nacional com {'≤'} 40% conteúdo importação</option>
                  <option value="6">6 — Estrangeira sem similar nacional</option>
                  <option value="7">7 — Estrangeira sem similar nacional (mercado interno)</option>
                  <option value="8">8 — Nacional com conteúdo de importação {'>'} 70%</option>
                </select>
              </div>
              <Input label="CFOP dentro do estado" placeholder="5102" value={form.cfop_dentro_estado} onChange={set("cfop_dentro_estado")} />
              <Input label="CFOP fora do estado" placeholder="6102" value={form.cfop_fora_estado} onChange={set("cfop_fora_estado")} />
              {form.regime_tributario === "simples_nacional" ? (
                <Input label="CSOSN (Simples Nacional)" placeholder="102" value={form.csosn} onChange={set("csosn")} />
              ) : (
                <Input label="CST ICMS" placeholder="40" value={form.cst_icms} onChange={set("cst_icms")} />
              )}
              <Input
                label="Código benefício fiscal (cBenef)"
                placeholder="SP810160 (para hortifruti em SP)"
                value={form.codigo_beneficio_fiscal || ""}
                onChange={set("codigo_beneficio_fiscal")}
                data-testid="company-cbenef"
              />
              <Input
                label="Série da NF-e"
                type="number"
                placeholder="1"
                value={form.serie_nfe ?? 1}
                onChange={set("serie_nfe")}
                data-testid="company-serie-nfe"
              />
              <div>
                <Input
                  label="Nº da última NF-e emitida"
                  type="number"
                  placeholder="0 = deixar a Focus gerenciar"
                  value={form.numero_ultima_nfe ?? 0}
                  onChange={set("numero_ultima_nfe")}
                  data-testid="company-numero-ultima-nfe"
                />
                <p className="text-[11px] text-ink-500 mt-1">
                  Preencha com o número da última NF-e emitida no sistema anterior. A próxima terá n° <strong>último + 1</strong>. Após cada autorização o sistema incrementa automaticamente.
                </p>
              </div>
            </div>
            <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-900 leading-relaxed">
              <strong>Cebola in natura</strong> (NCM 07031019) em muitos estados (ex.: SP) tem isenção de ICMS.
              Nesses casos, use <strong>CST 40</strong> (isenta) ou <strong>CST 41</strong> (não tributada) em vez de 00.
              Consulte seu contador para o CST/CFOP correto por operação.
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Identidade visual</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Cor primária</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.cor_primaria} onChange={set("cor_primaria")} className="w-12 h-12 rounded-md border border-[#D6D2CC]" />
                  <input value={form.cor_primaria} onChange={set("cor_primaria")} className="flex-1 rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900 font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Cor de acento (dourado)</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.cor_acento} onChange={set("cor_acento")} className="w-12 h-12 rounded-md border border-[#D6D2CC]" />
                  <input value={form.cor_acento} onChange={set("cor_acento")} className="flex-1 rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900 font-mono" />
                </div>
              </div>
            </div>

            {/* Política de folha */}
            <div className="mt-6 pt-6 border-t border-ink-200">
              <h3 className="font-heading font-bold text-ink-900 mb-3">Política de folha de pagamento</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Valor hora extra 50% (R$/h)</label>
                  <input
                    type="number" step="0.01"
                    value={form.valor_hora_extra_50 ?? 15}
                    onChange={set("valor_hora_extra_50")}
                    className="w-full rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900 font-mono"
                    data-testid="company-he50"
                  />
                  <p className="text-[11px] text-ink-500 mt-1">Usado em dias úteis (segunda a sábado)</p>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">Valor hora extra 100% (R$/h)</label>
                  <input
                    type="number" step="0.01"
                    value={form.valor_hora_extra_100 ?? 30}
                    onChange={set("valor_hora_extra_100")}
                    className="w-full rounded-md bg-white border border-[#D6D2CC] px-3 py-3 text-ink-900 font-mono"
                    data-testid="company-he100"
                  />
                  <p className="text-[11px] text-ink-500 mt-1">Usado em domingos e feriados</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-3">Pré-visualização</h3>
            <div className="border-2 border-stone-900 rounded-md p-4 bg-white">
              <div className="flex items-center gap-3 pb-3 border-b border-stone-900">
                {form.logo_url
                  ? <img src={form.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
                  : <div className="w-10 h-10 rounded" style={{ background: form.cor_primaria }}></div>}
                <div>
                  <div className="font-heading font-extrabold leading-tight" style={{ color: form.cor_primaria }}>{form.razao_social || "—"}</div>
                  <div className="text-[10px] tracking-wider" style={{ color: form.cor_acento }}>{form.cnpj || "—"}</div>
                </div>
              </div>
              <div className="text-[10px] text-ink-700 mt-2 leading-relaxed">
                {form.endereco_logradouro}<br/>
                {form.endereco_cidade && `${form.endereco_cidade}/${form.endereco_uf}`} {form.endereco_cep}
              </div>
            </div>

            {err && <div className="mt-4 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
            {saved && <div className="mt-4 text-sm text-ok bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md">✓ Salvo com sucesso</div>}
            <Button type="submit" size="lg" className="w-full mt-4" disabled={busy} data-testid="company-save-button">
              {busy ? <Spinner size={16} /> : <><Save size={16} /> Salvar</>}
            </Button>
          </Card>
        </div>
      </form>
    </Layout>
  );
}

function SaldoInput({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-sm font-mono">R$</span>
        <input
          type="number" step="0.01"
          value={value ?? 0}
          onChange={onChange}
          disabled={disabled}
          className={`w-full rounded-md border border-[#D6D2CC] pl-10 pr-3 py-3 font-mono text-lg ${disabled ? "bg-stone-100 text-ink-500 cursor-not-allowed" : "bg-white text-ink-900"}`}
          data-testid={testid}
        />
      </div>
    </div>
  );
}
